import { ReactElement } from 'react'

import { TFunction } from 'i18next'
import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import pluralize from 'pluralize'

import { date } from 'configuration/dayjs'
import { DATA_SOURCES } from 'configuration/widgets'
import { WidgetDataSourceKey } from 'const/dataSources'
import {
    ADVANCED_AUTOMATIONS,
    ADVANCED_PRODUCT_TITLE,
    ADVANCED_TEXT,
    COUNTRIES,
    DASHBOARD_NAME,
    DATES,
    GREATER_THAN_OR_EQUAL,
    HierarchicalFilterState,
    LABELS,
    LESS_THAN_OR_EQUAL,
    NUMBER_RANGE_OPERATOR_DISPLAY,
    RANGE_LAG,
    RANK_BETTER_THAN_OR_EQUAL,
    RANK_WORSE_THAN,
    RANK_WORSE_THAN_OR_EQUAL,
    RESOURCE_NAME_BY_FILTER_KEY,
    SEARCH_TERM,
    SEGMENT_BRANDS,
    SELLER_NAME,
    SOV_COUNTRIES,
    SOV_RANK,
} from 'const/filters'
import { InfoItem } from 'features/Dashboards/components/Widgets/InfoRow/InfoRow'
import {
    formatCompactDate,
    formatCompactDateRange,
    getDateRangeArray,
    getFormattedDateRange,
    getResolvedDateRangeLabel,
    ResolvedDateWindow,
} from 'helpers/dateRange'
import {
    getSingleClauseFilterLabel,
    getSingleClauseFilterValue,
} from 'helpers/filter'
import { getBooleanFilterLabel } from 'helpers/filters'
import { formatCerebroDate, getValueFormatter } from 'helpers/formatting'
import { isLabelValue, isMetricValue, shortNameToNode } from 'helpers/typeGuard'
import { elementToString } from 'helpers/utilities'
import { defaultDatesFilter } from 'reducers/ui/defaults'
import {
    DashboardParams,
    DateRangeFilter,
    FieldMapping,
    FilterHierarchy,
    FilterOption,
    FiltersHierarchy,
    FiltersState,
    FilterValue,
    SingleClauseFilterValue,
    TypeaheadOption,
    WidgetFilterMap,
} from 'types'

export type FilterChipLabelOptions = Partial<Record<string, TypeaheadOption[]>>

export const formatFilters = (filters: FiltersState): DashboardParams => {
    const params: DashboardParams = {}

    if (!isEmpty(filters[DASHBOARD_NAME])) {
        params.name__icontains = filters[DASHBOARD_NAME]
    }

    return params
}

export const getFiltersHierarchy = (
    tabLevelFilters: WidgetFilterMap,
    widgetLevelFilters: WidgetFilterMap,
    widgetDataSource: WidgetDataSourceKey
): FiltersHierarchy => {
    // Parameter is cloned to avoid side effects.
    // We always consider the date range filter even when it is not explicitly in the state.
    const tabFilters = cloneDeep(tabLevelFilters)
    tabFilters[DATES] = tabFilters[DATES] || defaultDatesFilter
    if (widgetDataSource === 'sov_search_results' && tabFilters[COUNTRIES]) {
        tabFilters[SOV_COUNTRIES] = tabFilters[COUNTRIES]
        delete tabFilters[COUNTRIES]
    }

    const hierarchy = {
        tabLevelFilters: {},
        widgetLevelFilters: {},
        result: {},
    } as FiltersHierarchy

    const applicableFilters = DATA_SOURCES[widgetDataSource]?.filters
    const {
        APPLIED,
        APPLIED_BY_TAB,
        APPLIED_BY_WIDGET,
        NOT_APPLICABLE,
        OVERRIDDEN_BY_WIDGET,
        OVERRIDES_TAB_VALUES,
    } = HierarchicalFilterState

    Object.keys(tabFilters).forEach((filterKey) => {
        // remove this after cleaning up the config in db
        if (filterKey === 'scIntegrations') {
            return
        }

        if (
            tabFilters[filterKey]?.length ||
            (filterKey === RANGE_LAG && Number.isInteger(tabFilters.rangeLag))
        ) {
            hierarchy.tabLevelFilters[filterKey] = {
                state: NOT_APPLICABLE,
                values: tabFilters[filterKey],
            }

            if (applicableFilters?.includes(filterKey)) {
                hierarchy.tabLevelFilters[filterKey].state = APPLIED
                hierarchy.result[filterKey] = {
                    state: APPLIED_BY_TAB,
                    values: tabFilters[filterKey],
                }
            }
        }
    })

    Object.keys(widgetLevelFilters).forEach((filterKey) => {
        // remove this after cleaning up the config in db
        if (filterKey === 'scIntegrations') {
            return
        }

        if (
            widgetLevelFilters[filterKey]?.length ||
            (filterKey === RANGE_LAG && widgetLevelFilters[DATES]?.length)
        ) {
            hierarchy.widgetLevelFilters[filterKey] = {
                state: APPLIED,
                values: widgetLevelFilters[filterKey],
            }
            hierarchy.result[filterKey] = {
                state: APPLIED_BY_WIDGET,
                values: widgetLevelFilters[filterKey],
            }

            if (hierarchy.tabLevelFilters[filterKey]) {
                hierarchy.tabLevelFilters[filterKey].state =
                    OVERRIDDEN_BY_WIDGET
                hierarchy.widgetLevelFilters[filterKey].state =
                    OVERRIDES_TAB_VALUES
                hierarchy.result[filterKey].state = OVERRIDDEN_BY_WIDGET
            }
        }
    })

    return hierarchy
}

export const formatFilterLabel = (
    key: string,
    values: FilterValue | undefined,
    t: TFunction,
    currencyCode: string,
    rangeLag?: number,
    metricsConfig?: FieldMapping,
    chipLabelOptions?: FilterChipLabelOptions,
    resolvedDateWindow?: ResolvedDateWindow
): string | ReactElement => {
    const resourceName = RESOURCE_NAME_BY_FILTER_KEY[key] || key
    // An empty value is as absent as a missing one: every branch below either
    // indexes into it or asks a date range for its years, and a cleared filter
    // has neither. Every other key already fell through to the same return.
    if (!values?.length) {
        return resourceName
    }

    if (key === DATES) {
        // The window the server reported wins over the one the user picked: on a
        // weekly source they differ, and the numbers beside this chip cover the
        // reported one. Absent (no response yet, or a daily source) the chip
        // renders exactly as it always has.
        if (resolvedDateWindow) {
            return getResolvedDateRangeLabel(
                values as DateRangeFilter,
                resolvedDateWindow,
                date(),
                rangeLag
            )
        }

        const formattedDateRange = getFormattedDateRange(
            values as DateRangeFilter,
            date(),
            false,
            rangeLag
        )

        return formattedDateRange ?? ''
    }

    if (key === SEARCH_TERM) {
        return `${resourceName}: ${(values[0] as FilterOption).value}`
    }

    if (
        [
            ADVANCED_AUTOMATIONS,
            ADVANCED_PRODUCT_TITLE,
            ADVANCED_TEXT,
            LABELS,
        ].includes(key)
    ) {
        return getBooleanFilterLabel(
            JSON.parse((values[0] as FilterOption).value as string),
            null,
            resourceName,
            'Rules',
            key !== LABELS
        )
    }

    if (key === SELLER_NAME || key === SEGMENT_BRANDS) {
        const result = getSingleClauseFilterValue(
            values as SingleClauseFilterValue[],
            'Unknown'
        )

        if (result) {
            const { operator, value, excludeUnknown } = result
            return getSingleClauseFilterLabel(
                operator,
                value,
                resourceName,
                chipLabelOptions?.[key] ?? [],
                excludeUnknown
            )
        }

        return resourceName
    }

    if (values.length === 1) {
        if (key === SOV_RANK) {
            const rankFilter = values[0] as FilterOption
            let operator = ''
            if (rankFilter.operator) {
                if (
                    [
                        GREATER_THAN_OR_EQUAL,
                        RANK_WORSE_THAN_OR_EQUAL,
                        RANK_WORSE_THAN,
                    ].includes(rankFilter.operator)
                ) {
                    operator = t(
                        'filters:NumberRangeControl.operatorLabels.rankWorseThan',
                        'Not in the top'
                    )
                } else if (
                    [LESS_THAN_OR_EQUAL, RANK_BETTER_THAN_OR_EQUAL].includes(
                        rankFilter.operator
                    )
                ) {
                    operator = t(
                        'filters:NumberRangeControl.operatorLabels.rankBetterThan',
                        'In the top'
                    )
                }
            }
            return `${resourceName}: ${operator} ${rankFilter.value}`
        }

        let theValue: unknown = values[0]
        if (isLabelValue(values[0])) {
            theValue = values[0].label
        } else if (isMetricValue(values[0])) {
            const formattedValue = getValueFormatter(
                metricsConfig?.[values[0].metric ?? '']?.metricOptions?.type,
                currencyCode,
                metricsConfig?.[values[0].metric ?? '']?.metricOptions
                    ?.shortFormat
            )(values[0].value)

            const metricKey = values[0].metric ?? ''
            const operator = values[0].operator
                ? NUMBER_RANGE_OPERATOR_DISPLAY[values[0].operator]
                : ''
            theValue = values[0].metric
                ? `${elementToString(shortNameToNode(metricsConfig?.[metricKey]?.shortName))} ${operator} ${formattedValue}`
                : ''
        }

        return `${resourceName}: ${theValue}`
    }

    if (values.length > 1) {
        if (isMetricValue(values[0])) {
            return `${pluralize(resourceName)}: ${values.length} Rules`
        }
        return `${pluralize(resourceName)}: ${values.length}`
    }

    return resourceName
}

export const formatFilterHierarchy = (
    hierarchy: FilterHierarchy,
    overrideText: string,
    t: TFunction,
    currencyCode: string,
    metricsConfig?: FieldMapping,
    notApplicableText?: string,
    chipLabelOptions?: FilterChipLabelOptions,
    resolvedDateWindow?: ResolvedDateWindow
): InfoItem[] => {
    const filterKeys = Object.keys(hierarchy)

    // The date range filter is supposed to always be the first one shown
    const dateIndex = filterKeys.indexOf(DATES)
    if (dateIndex > 0) {
        const dateItem = filterKeys.splice(dateIndex, 1)[0]
        filterKeys.unshift(dateItem)
    }

    const rangeLagIdx = filterKeys.indexOf(RANGE_LAG)
    let rangeLag = 0
    if (rangeLagIdx > 0) {
        filterKeys.splice(rangeLagIdx, 1)
        rangeLag = parseInt(hierarchy.rangeLag.values as string, 10)
    }

    return filterKeys.map((key) => {
        const filter = hierarchy[key]

        let hint
        if (filter.state === HierarchicalFilterState.OVERRIDDEN_BY_WIDGET) {
            hint = overrideText
        } else if (filter.state === HierarchicalFilterState.NOT_APPLICABLE) {
            hint = notApplicableText
        }

        // `filter.values?.length` covers both shapes a date filter takes — a
        // preset name and a pair of dates — and excludes the cleared filter,
        // which has no window to describe and no dates to resolve.
        const resolved =
            key === DATES && filter.values?.length
                ? resolvedDateWindow
                : undefined
        if (
            resolved &&
            filter.state !== HierarchicalFilterState.NOT_APPLICABLE
        ) {
            // On a mixed tab this chip is the only place the widening is visible,
            // so name both windows — the one selected and the one queried. Both
            // through one formatter, so the two halves of the sentence can be
            // compared and each agrees with the chip beside it.
            const requested = getDateRangeArray(
                filter.values as DateRangeFilter,
                false,
                rangeLag || undefined
            )
            const requestedLabel = formatCompactDateRange(
                formatCerebroDate(requested[0]),
                formatCerebroDate(requested[1])
            )
            const resolvedLabel = formatCompactDateRange(
                resolved.start,
                resolved.end
            )
            if (requestedLabel && requestedLabel !== resolvedLabel) {
                // Two different adjustments reach this point, and only one of them
                // is about the grain. A window can also end after the last week
                // the data covers, in which case it comes back *shorter* — saying
                // it was widened to whole weeks then is simply false, and it hides
                // the reason, which is that the data isn't there yet. "Adjusted"
                // is true either way and matches the picker's own banner.
                const heldAtBoundary =
                    resolved.end < formatCerebroDate(requested[1])
                const adjustment = heldAtBoundary
                    ? t('filters:DateRangeFilter.weeklyBoundaryHint', {
                          defaultValue:
                              'This data is weekly (Sunday–Saturday) and complete through {{end}}. {{requested}} was adjusted to {{resolved}}.',
                          requested: requestedLabel,
                          resolved: resolvedLabel,
                          end: formatCompactDate(resolved.end),
                      })
                    : t('filters:DateRangeFilter.weeklyGrainHint', {
                          defaultValue:
                              'This data is weekly (Sunday–Saturday). {{requested}} was adjusted to whole weeks: {{resolved}}.',
                          requested: requestedLabel,
                          resolved: resolvedLabel,
                      })
                // A widget-level date override already occupies the hint, and it
                // is the case where the dates on screen differ most from the ones
                // picked. Both facts are needed: why the window isn't the one it
                // names, and which filter won. In that order — the dates are what
                // prompts opening this — and on their own lines, which the
                // popover renders (`white-space: pre-line`).
                hint = hint ? `${adjustment}\n${hint}` : adjustment
            }
        }

        return {
            state: filter.state,
            label: formatFilterLabel(
                key,
                filter.values,
                t,
                currencyCode,
                key === DATES && rangeLag ? rangeLag : undefined,
                metricsConfig,
                chipLabelOptions,
                resolved
            ),
            hint,
        }
    })
}
